import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Icon from "../../../components/AppIcon"
import Input from "../../../components/ui/Input"
import Select from "../../../components/ui/Select"
import Button from "../../../components/ui/Button"
import ReauthModal from "../../../components/ui/ReauthModal"
import { Card, CardContent } from "../../../components/ui/card"
import { Badge } from "../../../components/ui/badge"
import { listCourses, createCourse, deleteCourse, updateCourse } from "../../../utils/courseHelpers"
import { syncCourseToCatalog } from "../../../utils/catalogHelpers"
import { computeCoursePerformance } from "../../../utils/coursePerformanceHelpers"

const COURSE_TYPE_OPTIONS = [
  { value: "course", label: "Course" },
  { value: "program", label: "Program" },
  { value: "track", label: "Track" },
  { value: "certification", label: "Certification" },
]

const STATUS_VARIANT = { draft: "warning", published: "success" }

const EditThumbnailModal = ({ orgId, organization, course, onClose, onSaved }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(course?.thumbnailUrl || "")
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState("")

  const handleSave = async (e) => {
    e?.preventDefault()
    setError("")
    setIsSaving(true)
    const cleanUrl = thumbnailUrl?.trim() || ""
    const { error: updateError } = await updateCourse(orgId, course?.courseId, { thumbnailUrl: cleanUrl })

    // The catalog keeps its own denormalized copy for public browsing — it's only
    // written once at publish time, so any edit to an already-published course has
    // to explicitly re-sync it here or the catalog silently goes stale.
        if (!updateError && course?.status === "published" && course?.visibility === "public") {
      await syncCourseToCatalog({
        orgId,
        orgName: organization?.name,
        orgVerified: organization?.verified,
        orgNesolearnId: orgId,
        course: { ...course, thumbnailUrl: cleanUrl },
      })
    }

    setIsSaving(false)
    if (updateError) {
      setError(updateError?.message || "Could not update cover image")
      return
    }
    onSaved()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <Card className="max-w-md w-full dark:bg-card">
        <div className="flex items-center justify-between px-5">
          <h3 className="text-lg font-semibold text-foreground">Cover Image</h3>
          <Button variant="ghost" size="icon" onClick={onClose} iconName="X" iconSize={18} />
        </div>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <Input
              label="Cover Image URL"
              placeholder="https://..."
              value={thumbnailUrl}
              onChange={(e) => setThumbnailUrl(e?.target?.value)}
              description={`For "${course?.title}"`}
            />
            <div className="rounded-md overflow-hidden border border-border h-28 bg-muted flex items-center justify-center">
              {thumbnailUrl?.trim() ? (
                <img
                  src={thumbnailUrl}
                  alt="Cover preview"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
              ) : (
                <Icon name="BookOpen" size={24} className="text-muted-foreground" />
              )}
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" fullWidth onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="default" fullWidth loading={isSaving}>Save</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

const CreateCourseModal = ({ orgId, currentUserUid, onClose, onCreated }) => {
  const [title, setTitle] = useState("")
  const [courseType, setCourseType] = useState("course")
  const [thumbnailUrl, setThumbnailUrl] = useState("")
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState("")

  const handleCreate = async (e) => {
    e?.preventDefault()
    setError("")
    if (!title?.trim()) {
      setError("Course title is required")
      return
    }
    setIsCreating(true)
    const { courseId, error: createError } = await createCourse({
      orgId,
      title,
      courseType,
      thumbnailUrl: thumbnailUrl?.trim() || "",
      createdByUid: currentUserUid,
    })
    setIsCreating(false)
    if (createError) {
      setError(createError?.message || "Could not create course")
      return
    }
    onCreated(courseId)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <Card className="max-w-md w-full dark:bg-card">
        <div className="flex items-center justify-between px-5">
          <h3 className="text-lg font-semibold text-foreground">New Course</h3>
          <Button variant="ghost" size="icon" onClick={onClose} iconName="X" iconSize={18} />
        </div>
        <CardContent>
          <form onSubmit={handleCreate} className="space-y-4">
            <Input label="Title" placeholder="e.g. Blockchain Compliance Fundamentals" value={title} onChange={(e) => setTitle(e?.target?.value)} required />
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Type</label>
              <Select options={COURSE_TYPE_OPTIONS} value={courseType} onChange={setCourseType} />
            </div>
            <Input
              label="Cover Image URL"
              placeholder="https://..."
              value={thumbnailUrl}
              onChange={(e) => setThumbnailUrl(e?.target?.value)}
              description="Shown on the course card in the catalog. You can add or change this later."
            />
            {thumbnailUrl?.trim() && (
              <div className="rounded-md overflow-hidden border border-border h-28 bg-muted">
                <img
                  src={thumbnailUrl}
                  alt="Cover preview"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
              </div>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" fullWidth onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="default" fullWidth loading={isCreating}>Create & Continue</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
]

const SORT_OPTIONS = [
  { value: "updated", label: "Recently updated" },
  { value: "title", label: "Title A–Z" },
  { value: "learners", label: "Most learners" },
  { value: "revenue", label: "Highest revenue" },
]

const CoursesPanel = ({ organization, currentUserUid }) => {
  const navigate = useNavigate()
  const [courses, setCourses] = useState([])
  const [statsByCourseId, setStatsByCourseId] = useState({})
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState(null)
  const [editingThumbnailCourse, setEditingThumbnailCourse] = useState(null)

  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [sortKey, setSortKey] = useState("updated")
  const [viewMode, setViewMode] = useState("grid")

  const loadCourses = async () => {
    setLoading(true)
    const { courses: list } = await listCourses(organization?.orgId)
    setCourses(list || [])
    const stats = await computeCoursePerformance(organization?.orgId, list || [])
    setStatsByCourseId(stats || {})
    setLoading(false)
  }

  useEffect(() => {
    if (organization?.orgId) loadCourses()
  }, [organization?.orgId])

  const handleCreated = (courseId) => {
    setShowCreateModal(false)
    navigate(`/organization-course-builder?orgId=${organization?.orgId}&courseId=${courseId}`)
  }

  const handleDelete = (courseId) => {
    if (!window.confirm("Delete this course? This cannot be undone.")) return
    setPendingDeleteId(courseId)
  }

  const confirmDelete = async () => {
    await deleteCourse(organization?.orgId, pendingDeleteId)
    setPendingDeleteId(null)
    loadCourses()
  }

  const moduleCount = (course) => course?.modules?.length || 0
  const lessonCount = (course) => course?.modules?.reduce((sum, m) => sum + (m?.lessons?.length || 0), 0) || 0

  const visibleCourses = courses
    ?.filter((course) => {
      if (statusFilter !== "all" && course?.status !== statusFilter) return false
      if (searchQuery?.trim() && !course?.title?.toLowerCase()?.includes(searchQuery.trim().toLowerCase())) return false
      return true
    })
    ?.sort((a, b) => {
      if (sortKey === "title") return (a?.title || "").localeCompare(b?.title || "")
      if (sortKey === "learners") return (statsByCourseId?.[b?.courseId]?.learners || 0) - (statsByCourseId?.[a?.courseId]?.learners || 0)
      if (sortKey === "revenue") return (statsByCourseId?.[b?.courseId]?.revenue || 0) - (statsByCourseId?.[a?.courseId]?.revenue || 0)
      return 0 // "updated" — listCourses already returns most-recently-updated first
    })

  return (
    <div className="space-y-6 md:col-span-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Courses</h2>
          <p className="text-sm text-muted-foreground">Create, organize and deliver learning experiences.</p>
        </div>
        <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowCreateModal(true)}>
          Create Course
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1">
          <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search courses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full rounded-md border border-border bg-background pl-9 pr-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
        <div className="w-full sm:w-44">
          <Select options={STATUS_FILTER_OPTIONS} value={statusFilter} onChange={setStatusFilter} />
        </div>
        <div className="w-full sm:w-48">
          <Select options={SORT_OPTIONS} value={sortKey} onChange={setSortKey} />
        </div>
        <div className="flex items-center border border-border rounded-md overflow-hidden shrink-0">
          <button
            type="button"
            onClick={() => setViewMode("grid")}
            className={`p-2 ${viewMode === "grid" ? "bg-accent/10 text-accent" : "text-muted-foreground hover:bg-muted/50"}`}
          >
            <Icon name="LayoutGrid" size={16} />
          </button>
          <button
            type="button"
            onClick={() => setViewMode("list")}
            className={`p-2 border-l border-border ${viewMode === "list" ? "bg-accent/10 text-accent" : "text-muted-foreground hover:bg-muted/50"}`}
          >
            <Icon name="List" size={16} />
          </button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        {visibleCourses?.length || 0} course{visibleCourses?.length === 1 ? "" : "s"}
      </p>

      {loading ? (
        <div className="p-12 text-center text-muted-foreground text-sm">Loading courses...</div>
      ) : courses?.length === 0 ? (
        <Card className="p-12 text-center dark:bg-transparent">
          <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="BookOpen" size={24} className="text-accent" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1">No courses yet</h3>
          <p className="text-muted-foreground text-sm mb-4">Create your first course to start building your curriculum.</p>
          <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowCreateModal(true)}>New Course</Button>
        </Card>
      ) : visibleCourses?.length === 0 ? (
        <Card className="p-12 text-center dark:bg-transparent">
          <p className="text-sm text-muted-foreground">No courses match your search or filter.</p>
        </Card>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleCourses?.map((course) => {
            const stats = statsByCourseId?.[course?.courseId] || { learners: 0, completion: 0, revenue: 0 }
            return (
              <Card key={course?.courseId} className="p-0 overflow-hidden gap-3 hover:border-accent/40 transition-colors dark:bg-transparent">
                <div className="h-28 bg-muted flex items-center justify-center relative group/thumb">
                  {course?.thumbnailUrl ? (
                    <img src={course?.thumbnailUrl} alt={course?.title} className="w-full h-full object-cover" />
                  ) : (
                    <Icon name="BookOpen" size={28} className="text-muted-foreground" />
                  )}
                  <button
                    type="button"
                    onClick={() => setEditingThumbnailCourse(course)}
                    className="absolute inset-0 bg-black/0 group-hover/thumb:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover/thumb:opacity-100"
                  >
                    <span className="text-white text-xs font-medium flex items-center gap-1.5 bg-black/60 px-2.5 py-1.5 rounded-md">
                      <Icon name="ImagePlus" size={13} />
                      {course?.thumbnailUrl ? "Change cover" : "Add cover"}
                    </span>
                  </button>
                </div>
                <CardContent className="pb-5 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-semibold text-foreground text-sm leading-snug line-clamp-2">{course?.title}</h4>
                    <Badge variant={STATUS_VARIANT?.[course?.status] || "warning"} className="shrink-0">
                      {course?.status === "published" ? "Published" : "Draft"}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Icon name="Users" size={12} /> {stats?.learners?.toLocaleString()} learners</span>
                    <span className="flex items-center gap-1"><Icon name="TrendingUp" size={12} /> {stats?.completion}% completion</span>
                    <span className="flex items-center gap-1"><Icon name="Layers" size={12} /> {moduleCount(course)} modules</span>
                    <span className="flex items-center gap-1"><Icon name="PlayCircle" size={12} /> {lessonCount(course)} lessons</span>
                  </div>

                  {!course?.pricing?.isFree && (
                    <div className="flex items-center justify-between text-xs border-t border-border pt-2">
                      <span className="text-muted-foreground">Revenue</span>
                      <span className="font-semibold text-foreground">${stats?.revenue?.toLocaleString()}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      variant="outline" size="sm" fullWidth iconName="Pencil" iconPosition="left"
                      onClick={() => navigate(`/organization-course-builder?orgId=${organization?.orgId}&courseId=${course?.courseId}`)}
                    >
                      Manage
                    </Button>
                    <Button variant="ghost" size="icon" iconName="Trash2" iconSize={16} onClick={() => handleDelete(course?.courseId)} />
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      ) : (
        <Card className="p-0 dark:bg-transparent overflow-hidden">
          <div className="divide-y divide-border">
            {visibleCourses?.map((course) => {
              const stats = statsByCourseId?.[course?.courseId] || { learners: 0, completion: 0, revenue: 0 }
              return (
                <div key={course?.courseId} className="flex items-center gap-4 px-4 py-3">
                  <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                    {course?.thumbnailUrl ? (
                      <img src={course?.thumbnailUrl} alt={course?.title} className="w-full h-full object-cover" />
                    ) : (
                      <Icon name="BookOpen" size={18} className="text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-foreground truncate">{course?.title}</span>
                      <Badge variant={STATUS_VARIANT?.[course?.status] || "warning"} className="shrink-0">
                        {course?.status === "published" ? "Published" : "Draft"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{moduleCount(course)} modules · {lessonCount(course)} lessons</p>
                  </div>
                  <div className="hidden sm:flex items-center gap-6 text-xs text-muted-foreground shrink-0">
                    <span>{stats?.learners?.toLocaleString()} learners</span>
                    <span>{stats?.completion}% completion</span>
                    {!course?.pricing?.isFree && <span className="font-semibold text-foreground">${stats?.revenue?.toLocaleString()}</span>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="outline" size="sm" iconName="Pencil" iconPosition="left"
                      onClick={() => navigate(`/organization-course-builder?orgId=${organization?.orgId}&courseId=${course?.courseId}`)}
                    >
                      Manage
                    </Button>
                    <Button variant="ghost" size="icon" iconName="Trash2" iconSize={16} onClick={() => handleDelete(course?.courseId)} />
                  </div>
                </div>
              )
            })}
          </div>
        </Card>
      )}

      {showCreateModal && (
        <CreateCourseModal orgId={organization?.orgId} currentUserUid={currentUserUid} onClose={() => setShowCreateModal(false)} onCreated={handleCreated} />
      )}

      {editingThumbnailCourse && (
        <EditThumbnailModal
          orgId={organization?.orgId}
          organization={organization}
          course={editingThumbnailCourse}
          onClose={() => setEditingThumbnailCourse(null)}
          onSaved={() => { setEditingThumbnailCourse(null); loadCourses() }}
        />
      )}

      <ReauthModal open={!!pendingDeleteId} label="delete this course" onConfirm={confirmDelete} onClose={() => setPendingDeleteId(null)} />
    </div>
  )
}

export default CoursesPanel